import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inp = "rounded-lg border border-slate-300 px-3 py-2 text-sm";

export default function EmailInstellingenTab() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState("");
  const [testing, setTesting] = useState("");
  const [sys, setSys] = useState(null);
  const [sysText, setSysText] = useState("");
  const [afwText, setAfwText] = useState("");
  const [sysSaving, setSysSaving] = useState(false);

  useEffect(() => {
    api.get("/email-automations").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/systeem-instellingen").then((r) => { setSys(r.data); setSysText((r.data.alertEmails || []).join(", ")); setAfwText((r.data.afwijkingEmails || []).join(", ")); }).catch(() => {});
  }, []);

  const saveSys = async () => {
    setSysSaving(true);
    try {
      const alertEmails = sysText.split(/[,\n;]/).map((s) => s.trim()).filter(Boolean);
      const afwijkingEmails = afwText.split(/[,\n;]/).map((s) => s.trim()).filter(Boolean);
      const r = await api.put("/systeem-instellingen", { backupTijd: sys.backupTijd, alertEmails, afwijkingEnabled: sys.afwijkingEnabled, afwijkingEmails });
      setSys(r.data); setSysText((r.data.alertEmails || []).join(", ")); setAfwText((r.data.afwijkingEmails || []).join(", "));
      toast.success("Systeeminstellingen opgeslagen");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSysSaving(false); }
  };

  const update = (key, patch) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const save = async (it) => {
    setSaving(it.key);
    try {
      const emails = it._emailsText !== undefined
        ? it._emailsText.split(/[,\n;]/).map((s) => s.trim()).filter(Boolean)
        : (it.emails || []);
      const r = await api.put("/email-automations", { key: it.key, enabled: it.enabled, tijd: it.tijd, emails, extra: it.extra || {} });
      setItems((xs) => xs.map((x) => (x.key === it.key ? { ...r.data, _emailsText: undefined } : x)));
      toast.success(`"${it.label}" opgeslagen`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving("");
    }
  };

  const test = async (it) => {
    setTesting(it.key);
    try {
      await api.post(`/email-automations/${it.key}/test`);
      toast.success("Test-e-mail verstuurd");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting("");
    }
  };

  if (!items) return <div className="p-6 text-slate-500" data-testid="email-loading">Laden…</div>;

  return (
    <div data-testid="email-instellingen" className="max-w-3xl space-y-4">
      <div>
        <h2 className="text-lg font-bold text-slate-800">E-mailinstellingen</h2>
        <p className="text-sm text-slate-500">Beheer zelf wanneer, of en naar wie de automatische e-mails worden verstuurd. De e-mails gaan op ongeveer 15 minuten nauwkeurig op het ingestelde tijdstip uit.</p>
      </div>
      {items.map((it) => (
        <div key={it.key} data-testid={`email-card-${it.key}`} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold text-slate-800">{it.label}</div>
              <div className="text-sm text-slate-500">{it.beschrijving}</div>
            </div>
            <label className="flex shrink-0 items-center gap-2 text-sm font-medium text-slate-600">
              <input type="checkbox" data-testid={`email-enabled-${it.key}`} className="h-4 w-4 accent-[#1C64B0]" checked={it.enabled} onChange={(e) => update(it.key, { enabled: e.target.checked })} />
              Aan
            </label>
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
              <span>Tijd (24-uurs)</span>
              <input type="time" data-testid={`email-tijd-${it.key}`} className={inp} value={it.tijd} onChange={(e) => update(it.key, { tijd: e.target.value })} />
            </label>
            {(it.velden || []).map((v) => (
              <label key={v.key} className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                <span>{v.label}</span>
                <input type="number" step="any" data-testid={`email-extra-${it.key}-${v.key}`} className={`${inp} w-28`} value={it.extra?.[v.key] ?? v.default} onChange={(e) => update(it.key, { extra: { ...it.extra, [v.key]: e.target.value } })} />
              </label>
            ))}
          </div>
          {it.per_user ? (
            <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-500">Deze mail gaat automatisch naar het eigen e-mailadres van elke sales-medewerker.</p>
          ) : (
            <label className="mt-3 flex flex-col gap-1 text-xs font-medium text-slate-600">
              <span>Ontvangers — meerdere mogelijk, scheiden met komma of nieuwe regel</span>
              <textarea data-testid={`email-ontvangers-${it.key}`} rows={2} className={`${inp} w-full font-mono`} value={it._emailsText !== undefined ? it._emailsText : (it.emails || []).join(", ")} onChange={(e) => update(it.key, { _emailsText: e.target.value })} placeholder="naam@bedrijf.nl, baas@bedrijf.nl" />
            </label>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button data-testid={`email-save-${it.key}`} disabled={saving === it.key} className="rounded-lg bg-[#1C64B0] px-4 py-2 text-sm font-semibold text-white hover:bg-[#154E8A] disabled:opacity-50" onClick={() => save(it)}>{saving === it.key ? "Opslaan…" : "Opslaan"}</button>
            <button data-testid={`email-test-${it.key}`} disabled={testing === it.key} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" onClick={() => test(it)}>{testing === it.key ? "Versturen…" : "Nu testen"}</button>
            {it.last_sent && <span className="text-xs text-slate-400">Laatst verstuurd: {it.last_sent}</span>}
          </div>
        </div>
      ))}
      {sys && (
        <div data-testid="systeem-instellingen" className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <div className="font-semibold text-slate-800">Systeeminstellingen</div>
          <div className="text-sm text-slate-500">Tijdstip van de dagelijkse automatische back-up en wie de veiligheids-/integriteitsmeldingen ontvangt.</div>
          <div className="mt-3 flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
              <span>Tijd dagelijkse back-up (24-uurs)</span>
              <input type="time" data-testid="sys-backup-tijd" className={inp} value={sys.backupTijd} onChange={(e) => setSys((s) => ({ ...s, backupTijd: e.target.value }))} />
            </label>
          </div>
          <label className="mt-3 flex flex-col gap-1 text-xs font-medium text-slate-600">
            <span>Ontvangers veiligheidsmeldingen — meerdere mogelijk, scheiden met komma</span>
            <textarea data-testid="sys-alert-emails" rows={2} className={`${inp} w-full font-mono`} value={sysText} onChange={(e) => setSysText(e.target.value)} placeholder="baas@bedrijf.nl, beheer@bedrijf.nl" />
          </label>
          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-800">Afwijkingsmelding weegformulieren</div>
                <div className="text-sm text-slate-500">Stuurt automatisch een e-mail met een volledig overzicht zodra een weegformulier wordt afgerond met een gewicht of totaalgemiddelde buiten de marge.</div>
              </div>
              <label className="flex shrink-0 items-center gap-2 text-sm font-medium text-slate-600">
                <input type="checkbox" data-testid="afwijking-enabled" className="h-4 w-4 accent-[#1C64B0]" checked={!!sys.afwijkingEnabled} onChange={(e) => setSys((s) => ({ ...s, afwijkingEnabled: e.target.checked }))} />
                Aan
              </label>
            </div>
            <label className="mt-3 flex flex-col gap-1 text-xs font-medium text-slate-600">
              <span>Ontvangers afwijkingsmelding — meerdere mogelijk, scheiden met komma</span>
              <textarea data-testid="afwijking-emails" rows={2} className={`${inp} w-full font-mono`} value={afwText} onChange={(e) => setAfwText(e.target.value)} placeholder="bedrijfsleiding@bedrijf.nl, kwaliteit@bedrijf.nl" />
            </label>
          </div>
          <div className="mt-3">
            <button data-testid="sys-save" disabled={sysSaving} className="rounded-lg bg-[#1C64B0] px-4 py-2 text-sm font-semibold text-white hover:bg-[#154E8A] disabled:opacity-50" onClick={saveSys}>{sysSaving ? "Opslaan…" : "Opslaan"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
